"use client";

import * as React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/utils/utils";
import { Input } from "../input/input";
import Logo from "../information/brand-logo";
import { Text } from "../information/text";


type TopbarTab = {
  id: string;
  label: string;
  href?: string;
  onClick?: () => void;
  className?: string;
};

export type TopbarProps = {
  title?: React.ReactNode;
  showSearch?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  tabs?: TopbarTab[];
  /** Controlled active tab id */
  activeTabId?: string;
  /** Uncontrolled initial tab id */
  defaultActiveTabId?: string;
  onTabChange?: (id: string) => void;
  /** Right side custom items (icons/buttons/etc) */
  rightItems?: React.ReactNode[];
  /** Optional leading slot (e.g., hamburger) */
  leading?: React.ReactNode;
  /** Override logo slot */
  logo?: React.ReactNode;
  className?: string;
};

export function Topbar({
  title,
  showSearch = true,
  searchPlaceholder = "Search…",
  searchValue,
  onSearchChange,
  tabs = [],
  activeTabId,
  defaultActiveTabId,
  onTabChange,
  rightItems = [],
  leading,
  logo,
  className,
}: TopbarProps) {
  const isControlled = typeof activeTabId === "string";
  const [uncontrolledActive, setUncontrolledActive] = React.useState<string | undefined>(
    defaultActiveTabId ?? tabs[0]?.id
  );
  const currentActive = isControlled ? activeTabId : uncontrolledActive;

  const setActive = (id: string) => {
    if (!isControlled) setUncontrolledActive(id);
    onTabChange?.(id);
  };

  const [internalSearch, setInternalSearch] = React.useState("");
  const searchVal = typeof searchValue === "string" ? searchValue : internalSearch;

  return (
    <div
      className={cn(
        " flex items-center justify-between px-6 py-3 gap-4",
        className
      )}
    >
      {/* Left cluster: leading, logo, title, tabs */}
      <div className="flex items-center gap-3 min-w-0">
        {leading ? (
          <div className="text-primary-foreground/90">{leading}</div>
        ) : null}

        <div className="bg-background rounded-full p-2 shrink-0">
          {logo ?? <Logo size={20} className="h-5 w-5" title="Logo" />}
        </div>

        {title ? (
          typeof title === "string" || typeof title === "number" ? (
            <Text as="div" className="font-semibold whitespace-nowrap">{title}</Text>
          ) : (
            title
          )
        ) : null}

        {tabs?.length ? (
          <nav className="ml-4 flex items-center gap-6">
            {tabs.map((tab) => {
              const isActive = currentActive === tab.id;
              const content = (
                <div
                  className={cn(
                    "relative cursor-pointer select-none text-sm text-primary-foreground/85 hover:text-primary-foreground transition-colors",
                    "after:absolute after:left-1/2 after:-translate-x-1/2 after:-bottom-1 after:h-0.5 after:w-8 after:rounded-full after:bg-transparent",
                    isActive && "text-primary-foreground after:bg-accent-foreground/90",
                    tab.className
                  )}
                  onClick={() => {
                    tab.onClick?.();
                    setActive(tab.id);
                  }}
                >
                  {typeof tab.label === "string" || typeof tab.label === "number" ? (
                    <Text as="p">{tab.label}</Text>
                  ) : (
                    tab.label
                  )}
                </div>
              );

              return tab.href ? (
                <Link key={tab.id} to={tab.href} className="no-underline">
                  {content}
                </Link>
              ) : (
                <button
                  key={tab.id}
                  type="button"
                  className="bg-transparent p-0 outline-none border-0"
                  onClick={() => {
                    tab.onClick?.();
                    setActive(tab.id);
                  }}
                >
                  {content}
                </button>
              );
            })}
          </nav>
        ) : null}
      </div>

      {/* Right cluster: search + icons */}
      <div className="flex items-center gap-4">
        {showSearch ? (
          <div className="w-[36rem] max-w-[55vw]">
            <Input
              className=" text-foreground"
              placeholder={searchPlaceholder}
              value={searchVal}
              onChange={(e) => {
                if (typeof searchValue !== "string") setInternalSearch(e.target.value);
                onSearchChange?.(e.target.value);
              }}
            />
          </div>
        ) : null}

        {rightItems?.map((item, idx) => (
          <div key={idx} className="">
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}
